import {
  Button,
  CircularProgress,
  IconButton,
  makeStyles,
  Paper,
  Typography,
} from '@material-ui/core'
import Card from 'common/Card'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { filterMountpoints } from 'store/slices/map-slice'
import { useMountpointGroupsList, useMountpointsList, useNewMountpoint } from '../api'
import { formatDurationSince } from '../format-helpers'
import { GroupForm } from '../GroupForm'
import { CircleIcon } from '../icons'
import { strings } from '../strings'
import { SearchPanel } from './SearchPanel'
import RoomIcon from '@material-ui/icons/Room'
import { useMapContext } from 'Map/context/MapProvider'

export function Mountpoints() {
  const classes = useStyles()
  const [query, setQuery] = useState('')
  const [group, setGroup] = useState<number | null>(null)
  const [creation, setCreation] = useState(false)
  const { mountpointGroupsList } = useMountpointGroupsList()
  const newMountpoint = useNewMountpoint()

  return (
    <>
      <Card title={strings.mountpoints}>
        <SearchPanel
          onChanged={(query, group) => {
            setQuery(query)
            setGroup(group)
          }}
          groups={mountpointGroupsList}
          showNew={creation}
          onNew={() => {
            newMountpoint(query, group)
              // .then((data) => console.log(data))
              .catch((err) => console.log(err.message))
          }}
        />
        <GroupForm groupId={group} groupType="mountpoint" />
      </Card>
      <Paper className={classes.result}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th>&nbsp;</th>
              <th>{strings.name}</th>
              <th>{strings.connectedTime}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <DataTable query={query} group={group} onEmptyResult={setCreation} />
          </tbody>
        </table>
      </Paper>
    </>
  )
}

function DataTable({ query, group, onEmptyResult }: any) {
  const classes = useStyles()

  const { flyToMountpointPosition } = useMapContext()

  const dispatch = useDispatch()
  const history = useHistory()
  const { mountpointsList, loading, error } = useMountpointsList(query, group)

  useEffect(() => {
    if (!query.length) onEmptyResult(false)
    else
      onEmptyResult(mountpointsList && (mountpointsList.length === 0 || mountpointsList.length > 1))
  }, [onEmptyResult, mountpointsList, query])

  useEffect(() => {
    if (!mountpointsList) return
    dispatch(filterMountpoints(mountpointsList.map((x) => x.id)))
    return () => {
      dispatch(filterMountpoints([]))
    }
  }, [mountpointsList])

  if (loading)
    return (
      <Row>
        <CircularProgress size={20} />
      </Row>
    )

  if (error)
    return (
      <Row>
        <Typography color="error">{error.message}</Typography>
      </Row>
    )

  if (!mountpointsList || !mountpointsList.length)
    return (
      <Row>
        <Typography color="error">{strings.notExists}</Typography>
      </Row>
    )

  return (
    <>
      {mountpointsList
        .sort((a, b) => b.connectedSince - a.connectedSince)
        .map((x) => (
          <tr key={x.id}>
            <td>
              <CircleIcon style={{ color: x.connectedSince ? 'green' : 'lightgray' }} />
            </td>
            <td>
              <Button onClick={() => history.push(`/mountpoint/${x.id}/info`)}>{x.name}</Button>
            </td>
            <td>{formatDurationSince(x.connectedSince)}</td>
            <td>
              {/* Mobile */}
              <IconButton
                onClick={() => {
                  history.push('/')
                  setTimeout(() => {
                    flyToMountpointPosition(x.id)
                  }, 250)
                }}
                className={classes.mobileLocation}
              >
                <RoomIcon />
              </IconButton>
              {/* Desktop */}
              <IconButton
                onClick={() => flyToMountpointPosition(x.id)}
                className={classes.desktopLocation}
              >
                <RoomIcon />
              </IconButton>
            </td>
          </tr>
        ))}
    </>
  )
}

function Row({ children }: any) {
  return (
    <tr>
      <td colSpan={3} style={{ textAlign: 'center' }}>
        {children}
      </td>
    </tr>
  )
}

const useStyles = makeStyles((theme) => ({
  search: {
    flex: '0 1 auto',
    width: '100%',
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    backgroundColor: '#fff',
    '& .MuiButtonBase-root': {
      padding: theme.spacing(0.75),
    },
    '& .tags': {
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
      '& .MuiButtonBase-root': {
        borderRadius: theme.spacing(2),
        marginRight: theme.spacing(1),
        fontSize: '0.6rem',
        fontWeight: 'normal',
        padding: `${theme.spacing(0.5)}px ${theme.spacing(2)}px`,
      },
    },
  },
  result: {
    flex: '1 1 auto',
    width: '100%',
    height: '100%',
    marginTop: theme.spacing(1),
    backgroundColor: '#fff',
    padding: theme.spacing(2),
    overflowY: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    '& tr > td': {
      paddingTop: theme.spacing(1),
      cursor: 'pointer',
    },
    // '& tr:hover td': {
    //   backgroundColor: '#f8f8f8',
    // },
    '& th': {
      borderBottom: '1px solid lightgray',
      fontWeight: 'normal',
      textAlign: 'left',
      paddingBottom: theme.spacing(0.5),
      '&:first-child': {
        width: theme.spacing(4),
        borderBottom: 'none',
      },
    },
    '& .MuiSvgIcon-root': {
      marginTop: theme.spacing(1),
    },
  },

  mobileLocation: {
    padding: 1,
    '& svg': {
      marginTop: 0,
    },
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  desktopLocation: {
    padding: 1,
    '& svg': {
      marginTop: 0,
    },
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'unset',
    },
  },
}))
